import type { PrimeTeamFn, PrimeWorkspaceFn } from '@/features/iam'
import {
  SearchJobsRequest,
  SearchJobScopeOption,
} from '@taxfyle/api-internal/internal/job_pb'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { collection } from '@/infra/collection'
import { type JobsApi } from '@/features/jobs/api/job-api'
import { type Job } from '@/features/jobs/Job'
import { SearchableJob } from '@/features/jobs/list/SearchableJob'
import { type PrimePublishedLegendFn } from '@/features/jobs/legends/PublishedLegendStore'
import {
  mapJobProtoToSearchableJobProto,
  mapJobToSearchableJobProps,
  mapSearchableJobProtoToProps,
  type SearchableJobMappingContext,
} from '@/features/jobs/list/searchable-job-mappers'
import { type PrimeConversationFn } from '@/features/messaging'
import { type Observable } from 'rxjs'
import { concatMap, filter, share } from 'rxjs/operators'
import { type CursorPaged } from '@/infra/paging'
import { Id } from '@/infra/id'
import { match } from '@/utils/pattern-match-util'
import { filterNonNil } from '@/utils/rx-util'
import { retryOnRpcError } from '@/utils/grpc-util'
import { resolveJobShortId } from '@/features/jobs/job-short-id'
import { JobSearchQueryStringBuilder } from '@/features/jobs/list/JobSearchQueryStringBuilder'
import TypeCase = RealtimeEvent.TypeCase

/**
 * Whose jobs to search: every job the user can see, or only their own.
 */
export type JobSearchScope = 'All' | 'Mine'

/**
 * Query for {@link SearchableJobStore.search}.
 */
export interface SearchJobsQuery {
  limit?: number
  after?: string | null
  orderBy?: string
  searchText?: string
  filter?: string
  /**
   * Narrows the search to jobs that have any of these users as members.
   */
  memberIds?: Id[]
  scope: JobSearchScope
}

export class SearchableJobStore {
  /**
   * Searchable jobs collection.
   */
  readonly jobs = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new SearchableJob(),
  })

  /**
   * Searchable jobs created or updated in realtime.
   */
  readonly jobEvents$: Observable<JobSearchEvent>

  constructor(
    private readonly _api: JobsApi,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _primeConversation: PromiseLike<PrimeConversationFn>,
    private readonly _primePublishedLegend: PrimePublishedLegendFn,
    private readonly _primeTeam: PrimeTeamFn,
    private readonly _getCurrentUserId: () => string | null,
    private readonly _events$: Observable<RealtimeEvent>,
    private readonly _localJobChanges$: Observable<Job>,
  ) {
    this.search = this.search.bind(this)
    this.findJobIdByShortId = this.findJobIdByShortId.bind(this)

    this.jobEvents$ = this._events$.pipe(
      // Only events relating to jobs.
      filter((event) => {
        switch (event.getTypeCase()) {
          case TypeCase.JOB_CREATED:
          case TypeCase.JOB_UPDATED:
            return true
        }
        return false
      }),
      concatMap((e) => this._mapJobRealtimeEvent(e)),
      filterNonNil(),
      share(),
    )

    this.jobEvents$.subscribe()
    this._localJobChanges$.subscribe((job) => {
      let searchableJob = this.jobs.get(job.id)

      searchableJob ??= this.jobs.set({ id: Id.assert(job.id) })

      searchableJob.set(mapJobToSearchableJobProps(job))
    })
  }

  /**
   * Fetches the total number of jobs matching the query.
   */
  async fetchTotal(query: SearchJobsQuery, signal?: AbortSignal) {
    const result = await retryOnRpcError(
      () => this._api.jobs.searchJobs(this._createSearchJobsRequest(query)),
      { signal },
    )

    return result.getTotalSize()
  }

  /**
   * Searches the jobs matching the query.
   */
  async search(
    query: SearchJobsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<SearchableJob>> {
    const result = await retryOnRpcError(
      () => this._api.jobs.searchJobs(this._createSearchJobsRequest(query)),
      { signal },
    )
    const context = await this._createMappingContext()
    signal?.throwIfAborted()

    return {
      data: result
        .getJobsList()
        .map((op) => this.jobs.set(mapSearchableJobProtoToProps(op, context))),
      total: result.getTotalSize(),
      next: result.getNextPageToken() || null,
    }
  }

  /**
   * Resolves a job short ID to its canonical job ID, or `Id.none` when no job
   * the current user can see has that short ID.
   */
  findJobIdByShortId(shortId: string): Promise<Id> {
    return resolveJobShortId((query) => this.search(query), shortId)
  }

  private _createSearchJobsRequest(query: SearchJobsQuery) {
    const req = new SearchJobsRequest()
    if (query.limit) {
      req.setPageSize(query.limit)
    }
    if (query.after) {
      req.setPageToken(query.after)
    }

    if (query.orderBy) {
      req.setOrderBy(query.orderBy)
    }

    if (query.searchText) {
      req.setSearchText(query.searchText)
    }

    const filter = [
      query.filter,
      query.memberIds &&
        new JobSearchQueryStringBuilder()
          .withMemberIds(query.memberIds)
          .build(),
    ]
      .filter(Boolean)
      .join(' AND ')
    if (filter) {
      req.setFilter(filter)
    }

    req.setScope(
      match(query.scope, {
        All: () => SearchJobScopeOption.ALL,
        Mine: () => SearchJobScopeOption.MINE,
      }),
    )

    return req
  }

  private async _createMappingContext(): Promise<SearchableJobMappingContext> {
    return {
      primeWorkspace: this._primeWorkspace,
      primeConversation: await this._primeConversation,
      primePublishedLegend: this._primePublishedLegend,
      currentUserId: Id(this._getCurrentUserId()),
    }
  }

  private async _mapJobRealtimeEvent(
    e: RealtimeEvent,
  ): Promise<JobSearchEvent | null> {
    switch (e.getTypeCase()) {
      case TypeCase.JOB_UPDATED: {
        const job = e.getJobUpdated()?.getJob()
        if (!job) {
          return null
        }

        return {
          type: 'JobUpdated',
          job: this.jobs.set(
            mapSearchableJobProtoToProps(
              mapJobProtoToSearchableJobProto(
                job,
                this._primeTeam,
                this._primeWorkspace,
              ),
              await this._createMappingContext(),
            ),
          ),
        }
      }
      case TypeCase.JOB_CREATED: {
        const job = e.getJobCreated()?.getJob()
        if (!job) {
          return null
        }

        return {
          type: 'JobCreated',
          job: this.jobs.set(
            mapSearchableJobProtoToProps(
              mapJobProtoToSearchableJobProto(
                job,
                this._primeTeam,
                this._primeWorkspace,
              ),
              await this._createMappingContext(),
            ),
          ),
        }
      }
    }
    return null
  }
}

/**
 * A searchable job created or updated in realtime.
 */
export type JobSearchEvent = JobUpdatedEvent | JobCreatedEvent

/**
 * Job was updated.
 */
export interface JobUpdatedEvent {
  type: 'JobUpdated'
  job: SearchableJob
}

/**
 * Job was created.
 */
export interface JobCreatedEvent {
  type: 'JobCreated'
  job: SearchableJob
}
